import { get, post } from "@/infra/rest";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tpraAssessmentsQueryKeys, tpraVendorsQueryKeys } from "@/shared/constants/QueryKeys";

// Types
export interface ExistingAssessment {
  id: string;
  name: string;
  assessment_step: number;
  total_assessment_steps: number;
  updated_at: string;
  status: "input_needed" | "in_progress" | "complete" | "needs_review";
  is_active: boolean;
  permissions: {
    owner: string;
  };
  multi_file_vendor_assessment: {
    vendor_name: string;
    product_name: string | null;
    vendor_logo_url: string | null;
  };
}

export interface CheckAssessmentResponse {
  success: boolean;
  message: string;
  assessments: ExistingAssessment[];
  member_has_access: boolean;
}

export interface ArchiveResponse {
  success: boolean;
  message: string;
}

// Create Assessment Types
export interface CreateTPRAAssessmentRequest {
  type: "multi_file_vendor_assessment";
  multi_file_vendor_assessment: {
    doc_type: "json";
    response_quality: "lite" | "speed" | "accuracy";
    source_urls: string[];
    vendor_name: string;
    product_name?: string;
    selected_control_sets: string[];
    questionnaire_id?: string;
    vendor_domain_url?: string;
    vendor_logo_url?: string;
    vendor_filled_intake_form: boolean;
    contact_email?: string;
    vendor_email?: string;
  };
}

export interface TPRAAssessmentStep {
  assessment_id: string;
  type: string;
  step: number;
  status: string;
  data: Record<string, unknown> | null;
  id: string;
  created_at: string;
  updated_at: string;
  version: number;
}

export interface TPRAAssessment {
  id: string;
  name: string;
  type: string;
  status: string;
  [key: string]: unknown;
}

export interface CreateTPRAAssessmentResponse {
  success: boolean;
  assessment: TPRAAssessment;
  steps: TPRAAssessmentStep[];
}

export type TPRAErrorCode =
  | "NETWORK_ERROR"
  | "ALREADY_ARCHIVED"
  | "ASSESSMENT_NOT_FOUND"
  | "CREATE_FAILED"
  | "UNKNOWN";

export interface TPRAError {
  code: TPRAErrorCode;
  message: string;
}

// API Functions
export async function checkAssessmentExists(
  vendorName: string,
  productName?: string
): Promise<CheckAssessmentResponse | null> {
  try {
    const url = `/v0/tpra/assessments/${encodeURIComponent(vendorName)}/exists`;
    return await get<CheckAssessmentResponse>({
      url,
      config: { params: productName ? { product_name: productName } : undefined },
    });
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null; // No existing assessment
    }
    throw error;
  }
}

export async function archiveAssessment(assessmentId: string): Promise<ArchiveResponse> {
  return post<Record<string, never>, ArchiveResponse>({
    url: `/v0/tpra/assessments/${assessmentId}/archive`,
    data: {},
  });
}

export async function createTPRAAssessment(
  request: CreateTPRAAssessmentRequest
): Promise<CreateTPRAAssessmentResponse> {
  return post<CreateTPRAAssessmentRequest, CreateTPRAAssessmentResponse>({
    url: "/v0/tpra/assessments/create",
    data: request,
  });
}

// Parse errors into TPRAError format
export function parseTPRAError(error: any): TPRAError {
  if (!error) {
    return { code: "UNKNOWN", message: "An unknown error occurred" };
  }

  // Network error
  if (error.message === "Network Error" || !error.response) {
    return {
      code: "NETWORK_ERROR",
      message: "Unable to connect to the server. Please check your connection and try again.",
    };
  }

  const status = error.response?.status;
  const errorMessage = error.response?.data?.message || error.response?.data?.error_message;

  // Already archived
  if (status === 400 && errorMessage?.toLowerCase().includes("archived")) {
    return {
      code: "ALREADY_ARCHIVED",
      message: "This assessment has already been archived.",
    };
  }

  // Assessment not found
  if (status === 404) {
    return {
      code: "ASSESSMENT_NOT_FOUND",
      message: "The assessment could not be found.",
    };
  }

  // Default error
  return {
    code: "UNKNOWN",
    message: errorMessage || "An error occurred. Please try again.",
  };
}

// Hooks
export function useCheckAssessmentExists() {
  return useMutation({
    mutationFn: ({ vendorName, productName }: { vendorName: string; productName?: string }) =>
      checkAssessmentExists(vendorName, productName),
  });
}

export function useArchiveAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: archiveAssessment,
    onSuccess: () => {
      // Invalidate TPRA lists to reflect archived status
      queryClient.invalidateQueries({ queryKey: tpraAssessmentsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: tpraVendorsQueryKeys.all });
    },
  });
}

export function useCreateTPRAAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTPRAAssessment,
    onSuccess: () => {
      // Invalidate TPRA lists to reflect new assessment
      queryClient.invalidateQueries({ queryKey: tpraAssessmentsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: tpraVendorsQueryKeys.all });
    },
  });
}
